import { useCallback, useEffect, useState } from 'react'
import { CalendarCheck, AlertCircle, Inbox } from 'lucide-react'
import { useGetWorklist } from '../hooks/backend/interviews'
import { useGetTodayView, useSetTodayView } from '../hooks/backend/today'
import { useInterviewDecisions } from '../hooks/useInterviewDecisions'
import { useManager } from '../hooks/useManager'
import { useAccess } from '../hooks/useAccess'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { toast } from '../lib/shadcn/sonner'
import { ManagerBanner } from '../components/interviews/ManagerBanner'
import { WorklistCard } from '../components/interviews/WorklistCard'
import { BookingAlerts } from '../components/interviews/BookingAlerts'
import { PlatformToday } from '../components/today/PlatformToday'
import type { OutcomeAction } from '../components/interviews/DecisionActions'
import type { WorklistRow } from '../utils/interviews'
import { asTodayView, countText, type TodayView } from '../utils/today'

const VIEW_LABEL: Record<TodayView, string> = {
  platform: 'Platform',
  mine: 'My interviews',
}

export default function Today() {
  const { trigger: loadView } = useGetTodayView<{ view: TodayView }>()
  const { trigger: saveView } = useSetTodayView<{ view: TodayView }>()
  // Null until the saved choice has loaded, so the page never flashes the wrong
  // view and then swaps.
  const [view, setView] = useState<TodayView | null>(null)
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    loadView({}, { skipCache: true })
      .then((r) => setView(asTodayView(r?.view)))
      // A failed read is not a reason to show nothing. My interviews is the
      // view that changes nothing for anybody.
      .catch(() => setView('mine'))
  }, [loadView])

  const choose = useCallback(
    (next: TodayView) => {
      if (next === view) return
      setView(next)
      setCount(null)
      saveView({ view: next }, { skipCache: true }).catch((err: unknown) => {
        // The view still switches for this visit; only remembering it failed.
        // Say so, rather than quietly opening on the old one next time.
        toast.error('Your choice of view was not saved', {
          description: err instanceof Error ? err.message : String(err),
        })
      })
    },
    [view, saveView],
  )

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-[hsl(var(--brand))]" />
            <h2 className="text-xl font-semibold text-foreground">Today</h2>
          </div>
          {view && (
            <span className="text-sm text-muted-foreground tabular-nums" data-testid="today-count">
              {countText(view, count)}
            </span>
          )}
        </div>

        <div
          role="tablist"
          aria-label="Today view"
          className="inline-flex w-full rounded-md border border-border bg-background p-0.5 sm:w-auto"
        >
          {(['platform', 'mine'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              disabled={view === null}
              onClick={() => choose(v)}
              className={cn(
                'flex-1 rounded-[5px] px-4 py-1.5 text-sm font-medium transition-colors sm:flex-none',
                view === v
                  ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {VIEW_LABEL[v]}
            </button>
          ))}
        </div>

        {view === null ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : view === 'platform' ? (
          <>
            <p className="text-sm text-muted-foreground">
              Everything across the cockpit that has to be cleared today, oldest first. Each item opens the page where
              it is cleared.
            </p>
            <PlatformToday onTotal={setCount} />
          </>
        ) : (
          <MyInterviews onTotal={setCount} />
        )}
      </div>
    </div>
  )
}

function MyInterviews({ onTotal }: { onTotal: (n: number | null) => void }) {
  const { linked, loading: managerLoading } = useManager()
  const { canAct, isViewer } = useAccess()
  const { data, loading, error, trigger } = useGetWorklist()
  const { submit, busyId } = useInterviewDecisions()

  // scope 'mine' is the manager it is booked with, or the second interviewer
  // on Interview 2, for admins as well. The rule lives in getWorklist.
  const load = useCallback(() => {
    trigger({ scope: 'mine' }, { skipCache: true })
  }, [trigger])

  useEffect(() => {
    load()
  }, [load])

  const items = (data as WorklistRow[] | undefined) ?? []
  const normal = items.filter((i) => i.likely_duplicate !== true)
  const duplicates = items.filter((i) => i.likely_duplicate === true)

  useEffect(() => {
    onTotal(data === undefined ? null : normal.length)
  }, [data, normal.length, onTotal])

  // Viewers can never act; managers/admins can act once linked to a person record.
  const cardsCanAct = canAct && linked

  const handleSubmit = useCallback(
    async (interviewId: string, action: OutcomeAction, notes: string | null) => {
      await submit(interviewId, action, notes, load)
    },
    [submit, load],
  )

  return (
    <>
      <p className="text-sm text-muted-foreground">
        Interviews you sit on yourself: the ones booked with you, and every Interview 2 where you are the second
        interviewer.
      </p>

      {/* Managers who aren't linked can't act. Viewers are read-only by design, so
          don't nag them about a manager link. */}
      {!isViewer && !managerLoading && !linked && <ManagerBanner />}

      {/* 7600: bookings on a Calendly calendar that never reached this list. */}
      <BookingAlerts />

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : loading && items.length === 0 ? (
        <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Loading your interviews…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">
          <Inbox className="h-8 w-8 opacity-40" />
          <p className="text-sm font-medium text-foreground">No interviews of yours need a decision</p>
          <p className="text-sm">You're all caught up. Handled interviews drop off this list.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="space-y-3">
            {normal.map((item) => (
              <WorklistCard
                key={item.interview_id}
                item={item}
                canAct={cardsCanAct}
                busy={busyId === item.interview_id}
                onSubmit={handleSubmit}
                onChanged={load}
              />
            ))}
          </div>

          {duplicates.length > 0 && (
            <div className="space-y-3">
              <div>
                <h3 className="text-base font-semibold text-foreground">Likely duplicates</h3>
                <p className="text-sm text-muted-foreground">
                  These applicants already appear further along the pipeline. Review and withdraw the stale card.
                </p>
              </div>
              {duplicates.map((item) => (
                <WorklistCard
                  key={item.interview_id}
                  item={item}
                  canAct={cardsCanAct}
                  busy={busyId === item.interview_id}
                  onSubmit={handleSubmit}
                  onChanged={load}
                  isDuplicate
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
